import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import ProductCard from '@/components/ProductCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import HackCard from '@/components/HackCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, Repeat, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function NextStepsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openProduct, setOpenProduct] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .get('/recommendations/next')
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) return null;

  return (
    <div data-testid="next-steps-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Personalised next steps
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          What to try next.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Based on what you’ve marked as <em className="text-foreground not-italic">tried</em>, your skin type and your
          region—here are the smart next pieces to add to your ritual.
        </p>
      </div>

      {loading ? (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-80 rounded-2xl" />
          ))}
        </div>
      ) : !data || data.tried_count === 0 ? (
        <Card className="mt-10 rounded-2xl bg-card border-border/70 p-8 text-center">
          <Sparkles className="h-9 w-9 mx-auto text-rose-gold" />
          <p className="mt-3 font-display text-2xl text-ink">No “tried” items yet.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Visit any hack or product, click <em className="not-italic font-medium">I tried this</em>, and we’ll start
            building your personalized next steps.
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/diy">Browse DIY Lab</Link>
            </Button>
            <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/products">Open Curator</Link>
            </Button>
          </div>
        </Card>
      ) : (
        <div className="mt-10 space-y-12">
          {data.next_step?.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Layers className="h-4 w-4 text-rose-gold" />
                <h2 className="font-display text-2xl font-semibold text-ink">Smart next step</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5 max-w-xl">
                Pieces that pair with what you’ve already tried—e.g. add a hydrating serum after exfoliating, finish with SPF in the morning.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {data.next_step.map((p) => (
                  <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
                ))}
              </div>
            </section>
          )}
          {data.similar?.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Repeat className="h-4 w-4 text-rose-gold" />
                <h2 className="font-display text-2xl font-semibold text-ink">More like what you loved</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {data.similar.map((p) => (
                  <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
                ))}
              </div>
            </section>
          )}
          {data.hacks?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl font-semibold text-ink mb-4">DIY rituals to pair</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {data.hacks.map((h) => (
                  <HackCard key={h.id} hack={h} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </div>
  );
}
